import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, Home, Award, Users, Info, Trophy, User as UserIcon, LogOut, LayoutDashboard } from 'lucide-react';

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const navItems = [
    { label: 'Home', icon: Home, href: '/', isRoute: true },
    { label: 'Categories', icon: Award, href: '/categories', isRoute: true },
    { label: 'Jury', icon: Users, href: '/jury', isRoute: true },
    { label: 'About', icon: Info, href: '/about', isRoute: true }
  ];

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200" data-testid="site-header">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 font-extrabold text-xl" data-testid="site-logo-link">
          <div className="h-10 w-10 rounded-lg bg-amber-500 flex items-center justify-center" aria-hidden="true">
            <Trophy className="h-6 w-6 text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-gray-900">Stand Vision Awards</span>
            <span className="text-xs text-amber-600 font-semibold">Excellence in Exhibition Design</span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8" aria-label="Primary" data-testid="desktop-nav">
          {navItems.map((item) => (
            item.isRoute ? (
              <Link
                key={item.label}
                to={item.href}
                className="relative text-sm font-medium text-gray-700 hover:text-black nav-link-animated"
                data-testid={`nav-link-${item.label.toLowerCase()}`}
              >
                <span className="flex items-center gap-1.5">
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </span>
              </Link>
            ) : (
              <a
                key={item.label}
                href={item.href}
                className="relative text-sm font-medium text-gray-700 hover:text-black nav-link-animated"
                data-testid={`nav-link-${item.label.toLowerCase()}`}
              >
                <span className="flex items-center gap-1.5">
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </span>
              </a>
            )
          ))}
        </nav>

        {/* CTA and Mobile Menu */}
        <div className="flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="relative h-10 w-10 rounded-full"
                  data-testid="user-menu-button"
                >
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="h-10 w-10 rounded-full border-2 border-orange-200"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                      <UserIcon className="h-5 w-5 text-orange-600" />
                    </div>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" data-testid="user-dropdown-menu">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{user.name}</p>
                    <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate('/dashboard')}
                  data-testid="dropdown-dashboard"
                >
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  Dashboard
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={async () => {
                    await logout();
                    navigate('/');
                  }}
                  data-testid="dropdown-logout"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login">
              <Button
                className="bg-black hover:bg-gray-900 text-white rounded-[10px] px-6 h-10 font-medium shadow-sm hover:shadow-md transition-all duration-200"
                data-testid="sign-in-button"
              >
                Sign In / Sign Up
              </Button>
            </Link>
          )}

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="outline" size="icon" data-testid="mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px]" data-testid="mobile-menu">
              <div className="flex flex-col gap-4 mt-8">
                {navItems.map((item) => (
                  item.isRoute ? (
                    <Link
                      key={item.label}
                      to={item.href}
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-3 text-base font-medium text-gray-700 hover:text-amber-600 p-3 rounded-lg hover:bg-amber-50 transition-colors"
                      data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                    >
                      <item.icon className="h-5 w-5" />
                      {item.label}
                    </Link>
                  ) : (
                    <a
                      key={item.label}
                      href={item.href}
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-3 text-base font-medium text-gray-700 hover:text-amber-600 p-3 rounded-lg hover:bg-amber-50 transition-colors"
                      data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                    >
                      <item.icon className="h-5 w-5" />
                      {item.label}
                    </a>
                  )
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};